import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiSave, FiUsers, FiCalendar, FiClock, FiFileText, FiEdit2, FiX, FiList } from 'react-icons/fi';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';

const ManualAttendance = () => {
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]); // Store all employees
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [showAlert, setShowAlert] = useState('');
  const [alertType, setAlertType] = useState('success');

  // View mode: 'add', 'view'
  const [viewMode, setViewMode] = useState('add');
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState([]); // Store all fetched records
  const [editingRecord, setEditingRecord] = useState(null);

  // Filter state for viewing records
  const [filters, setFilters] = useState({
    department: '',
    employee: '',
    search: '',
    startDate: '',
    endDate: '',
    status: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    department: '',
    employee: '',
    date: '',
    status: 'Present',
    inTime: '',
    outTime: '',
    notes: ''
  });

  const [companyId, setCompanyId] = useState('');
  const [token, setToken] = useState('');

  // Helper function to format date without timezone issues
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Otherwise parse the date and format it using UTC to avoid timezone shifts
    const date = new Date(dateStr);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for display in table (MM/DD/YYYY)
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return '-';
    // Extract date parts from string or Date object using UTC to avoid timezone shifts
    let year, month, day;
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      [year, month, day] = dateStr.split('T')[0].split('-');
    } else {
      const date = new Date(dateStr);
      // Use UTC methods to avoid timezone conversion
      year = date.getUTCFullYear();
      month = String(date.getUTCMonth() + 1).padStart(2, '0');
      day = String(date.getUTCDate()).padStart(2, '0');
    }
    return `${month}/${day}/${year}`;
  };

  // Status options
  const statusOptions = [
    { value: 'Present', label: 'Present', color: 'bg-green-100 text-green-800' },
    { value: 'Absent', label: 'Absent', color: 'bg-red-100 text-red-800' },
    { value: 'Half Day', label: 'Half Day', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'Leave', label: 'On Leave', color: 'bg-blue-100 text-blue-800' },
    { value: 'Work From Home', label: 'Work From Home', color: 'bg-purple-100 text-purple-800' },
    { value: 'Holiday', label: 'Holiday', color: 'bg-gray-100 text-gray-800' }
  ];

  // Apply client-side filters (search and status)
  const applyClientSideFilters = (records) => {
    let filtered = [...records];

    // Filter by search (employee name)
    if (filters.search && filters.search.trim() !== '') {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(record => {
        const empName = record.employee?.firstName && record.employee?.lastName
          ? `${record.employee.firstName} ${record.employee.lastName}`.toLowerCase()
          : (record.employee?.name || record.employee?.teamMemberName || '').toLowerCase();
        return empName.includes(searchLower);
      });
    }

    // Filter by status
    if (filters.status && filters.status !== '') {
      filtered = filtered.filter(record => record.status === filters.status);
    }

    console.log(`Applied client-side filters: ${filtered.length} records from ${records.length}`);
    setAttendanceRecords(filtered);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      try {
        const decoded = jwtDecode(storedToken);
        console.log('Decoded token:', decoded);

        // Get company ID based on role
        let compId;
        if (decoded.role === 'company') {
          compId = decoded.userId || decoded.id;
        } else if (decoded.role === 'employee') {
          compId = decoded.companyId || decoded.company?._id || decoded.company;
        } else {
          compId = decoded.companyId || decoded.company?._id || decoded.id;
        }

        console.log('Company ID:', compId);
        setCompanyId(compId);

        if (compId) {
          fetchDepartments(storedToken, compId);
          fetchEmployees(storedToken, compId);
        } else {
          showNotification('Company ID not found in session', 'error');
        }
      } catch (error) {
        console.error('Token decode error:', error);
        showNotification('Invalid session. Please log in again.', 'error');
      }
    } else {
      showNotification('No session found. Please log in.', 'error');
    }

    // Set today's date as default
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, date: today }));

    // Set default filter dates (current month)
    const firstDayOfMonth = new Date();
    firstDayOfMonth.setDate(1);
    setFilters(prev => ({
      ...prev,
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today
    }));
  }, []);

  // Apply client-side filters when search or status changes
  useEffect(() => {
    if (allAttendanceRecords.length > 0) {
      applyClientSideFilters(allAttendanceRecords);
    }
  }, [filters.search, filters.status]);

  const fetchEmployees = async (authToken, compId) => {
    setLoadingEmployees(true);
    try {
      console.log('Fetching employees for company:', compId);
      const response = await axios.get(`https://slateblue-mallard-430836.hostingersite.com/api/employees/company/${compId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Employee response:', response.data);

      // Handle different response formats
      let employeeList = [];
      if (Array.isArray(response.data)) {
        employeeList = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        employeeList = response.data.data;
      } else if (response.data?.employees && Array.isArray(response.data.employees)) {
        employeeList = response.data.employees;
      }

      console.log('Employee list:', employeeList);
      setAllEmployees(employeeList); // Store all employees
      setEmployees(employeeList); // Initially show all

      if (employeeList.length === 0) {
        showNotification('No employees found for this company', 'warning');
      } else {
        console.log(`✅ Successfully loaded ${employeeList.length} employees`);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      showNotification(error.response?.data?.message || 'Failed to load employees', 'error');
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchDepartments = async (authToken, compId) => {
    setLoadingDepartments(true);
    try {
      console.log('Fetching departments for company:', compId);
      const response = await axios.get(`https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${compId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Department response:', response.data);
      const deptList = Array.isArray(response.data) ? response.data : [];
      setDepartments(deptList);

      if (deptList.length === 0) {
        showNotification('No departments found', 'warning');
      } else {
        console.log(`✅ Successfully loaded ${deptList.length} departments`);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      showNotification(error.response?.data?.message || 'Failed to load departments', 'error');
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Filter employees by department
  const filterEmployeesByDepartment = (departmentId) => {
    if (!departmentId) {
      // Show all employees if no department selected
      setEmployees(allEmployees);
      return;
    }

    // Find the selected department
    const selectedDept = departments.find(d => d._id === departmentId);
    if (!selectedDept) {
      setEmployees(allEmployees);
      return;
    }

    console.log('Filtering by department:', selectedDept);
    console.log('All employees:', allEmployees);

    // Filter employees who have this department (check both ID and name)
    const filtered = allEmployees.filter(emp => {
      console.log('Employee department:', emp.department);

      // Check if department is an array
      if (Array.isArray(emp.department)) {
        // Check if array contains the department ID or name
        return emp.department.some(dept => {
          // If dept is an object with _id property
          if (typeof dept === 'object' && dept !== null && dept._id) {
            return dept._id === departmentId || dept._id === selectedDept._id;
          }
          // If dept is a string (could be ID or name)
          return dept === departmentId || dept === selectedDept._id || dept === selectedDept.name;
        });
      }

      // If department is an object with _id property
      if (typeof emp.department === 'object' && emp.department !== null && emp.department._id) {
        return emp.department._id === departmentId || emp.department._id === selectedDept._id;
      }

      // If department is a string (could be ID or name)
      return emp.department === departmentId || emp.department === selectedDept._id || emp.department === selectedDept.name;
    });

    console.log(`Filtered ${filtered.length} employees for department: ${selectedDept.name}`);
    setEmployees(filtered);
  };

  const showNotification = (message, type = 'success') => {
    setShowAlert(message);
    setAlertType(type);
    setTimeout(() => setShowAlert(''), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If department changes, filter employees and reset employee selection
    if (name === 'department') {
      filterEmployeesByDepartment(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        employee: '' // Reset employee selection
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };



  const validateForm = (data) => {
    if (!data.department) {
      showNotification('Please select a department', 'error');
      return false;
    }
    if (!data.employee) {
      showNotification('Please select an employee', 'error');
      return false;
    }
    if (!data.date) {
      showNotification('Please select a date', 'error');
      return false;
    }
    if (!data.status) {
      showNotification('Please select a status', 'error');
      return false;
    }
    if (!data.inTime) {
      showNotification('Please enter in time', 'error');
      return false;
    }
    if (!data.outTime) {
      showNotification('Please enter out time', 'error');
      return false;
    }

    // Validate times
    if (data.inTime && data.outTime) {
      const inDateTime = new Date(`${data.date}T${data.inTime}`);
      const outDateTime = new Date(`${data.date}T${data.outTime}`);

      if (outDateTime <= inDateTime) {
        showNotification('Out time must be after in time', 'error');
        return false;
      }
    }

    return true;
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm(formData)) return;

    setLoading(true);

    try {
      const attendanceData = {
        employee: formData.employee,
        date: formData.date,
        status: formData.status,
        inLocation: 'Manual Entry',
        outLocation: 'Manual Entry'
      };

      // Add times if provided
      if (formData.inTime) {
        attendanceData.inTime = new Date(`${formData.date}T${formData.inTime}`).toISOString();
      }
      if (formData.outTime) {
        attendanceData.outTime = new Date(`${formData.date}T${formData.outTime}`).toISOString();
      }

      const response = await axios.post(
        'https://slateblue-mallard-430836.hostingersite.com/api/attendance/manual',
        attendanceData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      showNotification('Attendance added successfully!', 'success');

      // Reset form
      setFormData({
        department: '',
        employee: '',
        date: new Date().toISOString().split('T')[0],
        status: 'Present',
        inTime: '',
        outTime: '',
        notes: ''
      });
      // Reset employee filter to show all
      setEmployees(allEmployees);
    } catch (error) {
      console.error('Error adding attendance:', error);
      const errorMsg = error.response?.data?.message || 'Failed to add attendance';

      // Check if it's a duplicate entry error
      if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
        // Fetch the existing record and switch to edit mode
        fetchAndEditExistingRecord(formData.employee, formData.date);
      } else {
        showNotification(errorMsg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };



  const fetchAttendanceRecords = async () => {
    setLoading(true);
    try {
      let url = `https://slateblue-mallard-430836.hostingersite.com/api/attendance?company=${companyId}`;

      // Add only backend-supported filters (employee and date range)
      if (filters.employee) url += `&employee=${filters.employee}`;
      if (filters.startDate) url += `&startDate=${filters.startDate}`;
      if (filters.endDate) url += `&endDate=${filters.endDate}`;

      console.log('Fetching attendance records from:', url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Attendance response:', response.data);

      // Handle different response formats
      let records = [];
      if (Array.isArray(response.data)) {
        records = response.data;
      } else if (response.data?.records && Array.isArray(response.data.records)) {
        records = response.data.records;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        records = response.data.data;
      } else if (response.data?.attendanceRecords && Array.isArray(response.data.attendanceRecords)) {
        records = response.data.attendanceRecords;
      }

      console.log('Parsed records:', records);
      setAllAttendanceRecords(records);

      // Apply client-side filters
      applyClientSideFilters(records);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      showNotification(error.response?.data?.message || 'Failed to load attendance records', 'error');
      setAllAttendanceRecords([]);
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);

    // Find employee's department
    const empId = record.employee?._id || record.employee;
    const emp = allEmployees.find(e => e._id === empId);
    let deptId = '';

    if (emp && emp.department) {
      // Get the first department from employee's department array
      const empDeptName = Array.isArray(emp.department) ? emp.department[0] : emp.department;
      const dept = departments.find(d => d.name === empDeptName);
      if (dept) {
        deptId = dept._id;
        // Filter employees for this department
        filterEmployeesByDepartment(deptId);
      }
    }

    setFormData({
      department: deptId,
      employee: empId,
      date: formatDateForDisplay(record.date),
      status: record.status,
      inTime: record.inTime ? new Date(record.inTime).toTimeString().slice(0, 5) : '',
      outTime: record.outTime ? new Date(record.outTime).toTimeString().slice(0, 5) : '',
      notes: ''
    });
    setViewMode('add');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!validateForm(formData)) return;

    setLoading(true);

    try {
      const attendanceData = {
        employee: formData.employee,
        date: formData.date,
        status: formData.status,
        inLocation: 'Manual Entry',
        outLocation: 'Manual Entry'
      };

      if (formData.inTime) {
        attendanceData.inTime = new Date(`${formData.date}T${formData.inTime}`).toISOString();
      }
      if (formData.outTime) {
        attendanceData.outTime = new Date(`${formData.date}T${formData.outTime}`).toISOString();
      }

      await axios.put(
        `https://slateblue-mallard-430836.hostingersite.com/api/attendance/manual/${editingRecord._id}`,
        attendanceData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      showNotification('Attendance updated successfully!', 'success');

      // Reset form and editing state
      setEditingRecord(null);
      setFormData({
        department: '',
        employee: '',
        date: new Date().toISOString().split('T')[0],
        status: 'Present',
        inTime: '',
        outTime: '',
        notes: ''
      });
      // Reset employee filter to show all
      setEmployees(allEmployees);

      // Refresh records if in view mode
      if (viewMode === 'view') {
        fetchAttendanceRecords();
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      showNotification(error.response?.data?.message || 'Failed to update attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) {
      return;
    }

    setLoading(true);

    try {
      await axios.delete(
        `https://slateblue-mallard-430836.hostingersite.com/api/attendance/manual/${recordId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      showNotification('Attendance deleted successfully!', 'success');
      fetchAttendanceRecords();
    } catch (error) {
      console.error('Error deleting attendance:', error);
      showNotification(error.response?.data?.message || 'Failed to delete attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingRecord(null);
    setFormData({
      department: '',
      employee: '',
      date: new Date().toISOString().split('T')[0],
      status: 'Present',
      inTime: '',
      outTime: '',
      notes: ''
    });
    // Reset employee filter to show all
    setEmployees(allEmployees);
  };

  const fetchAndEditExistingRecord = async (employeeId, date) => {
    try {
      setLoading(true);

      // Fetch attendance records for this employee and date
      const response = await axios.get(
        `https://slateblue-mallard-430836.hostingersite.com/api/attendance?company=${companyId}&employee=${employeeId}&startDate=${date}&endDate=${date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle different response formats
      let records = [];
      if (Array.isArray(response.data)) {
        records = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        records = response.data.data;
      }

      if (records && records.length > 0) {
        const record = records[0];
        handleEdit(record);
        showNotification('Record found! You can now edit it.', 'info');
      } else {
        showNotification('Attendance already exists but could not be retrieved. Please use View Records to edit.', 'warning');
      }
    } catch (error) {
      console.error('Error fetching existing record:', error);
      showNotification('Please use View Records tab to edit the existing attendance.', 'warning');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e._id === empId);
    if (!emp) return '';
    return emp.firstName && emp.lastName
      ? `${emp.firstName} ${emp.lastName}`
      : emp.name || emp.teamMemberName || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {showAlert && (
        <AlertBox
          message={showAlert}
          type={alertType}
          onClose={() => setShowAlert('')}
        />
      )}

      {/* Mode Toggle */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex gap-4">
          <button
            onClick={() => {
              setViewMode('add');
              cancelEdit();
            }}
            className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${viewMode === 'add'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <FiPlus /> Add Entry
          </button>
          <button
            onClick={() => {
              setViewMode('view');
              fetchAttendanceRecords();
              cancelEdit();
            }}
            className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${viewMode === 'view'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <FiList /> View Records
          </button>
        </div>
      </div>

      {/* Single Entry Form */}
      {viewMode === 'add' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {editingRecord && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex justify-between items-center">
              <span className="text-yellow-800 font-medium">Editing Attendance Record</span>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-yellow-800 hover:text-yellow-900"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          )}
          <form onSubmit={editingRecord ? handleUpdate : handleSingleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FiUsers className="text-purple-600" />
                  Department *
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={loadingDepartments}
                >
                  <option value="">
                    {loadingDepartments ? 'Loading departments...' : departments.length === 0 ? 'No departments found' : 'Select Department'}
                  </option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FiUsers className="text-blue-600" />
                  Employee *
                </label>
                <select
                  name="employee"
                  value={formData.employee}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={loadingEmployees}
                >
                  <option value="">
                    {loadingEmployees ? 'Loading employees...' : employees.length === 0 ? 'No employees found' : 'Select Employee'}
                  </option>
                  {employees.map(emp => {
                    const empName = emp.firstName && emp.lastName
                      ? `${emp.firstName} ${emp.lastName}`
                      : emp.name || emp.teamMemberName || 'Unknown';
                    const empEmail = emp.email || '';
                    return (
                      <option key={emp._id} value={emp._id}>
                        {empName} {empEmail ? `(${empEmail})` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FiCalendar className="text-blue-600" />
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* In Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FiClock className="text-green-600" />
                  In Time *
                </label>
                <input
                  type="time"
                  name="inTime"
                  value={formData.inTime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Out Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FiClock className="text-red-600" />
                  Out Time *
                </label>
                <input
                  type="time"
                  name="outTime"
                  value={formData.outTime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add any additional notes..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex justify-end gap-3">
              {editingRecord && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-500 text-white px-8 py-3 rounded-lg hover:bg-gray-600 flex items-center gap-2 font-medium shadow-md transition-all"
                >
                  <FiX />
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-md transition-all"
              >
                <FiSave />
                {loading ? (editingRecord ? 'Updating...' : 'Saving...') : (editingRecord ? 'Update Attendance' : 'Save Attendance')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* View/Edit Records */}
      {viewMode === 'view' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Attendance Records</h2>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            {/* Search Box */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by employee name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Department Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                value={filters.department}
                onChange={(e) => {
                  const deptId = e.target.value;
                  setFilters({ ...filters, department: deptId, employee: '' });
                  filterEmployeesByDepartment(deptId);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
              <select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={filters.department && employees.length === 0}
              >
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.firstName && emp.lastName
                      ? `${emp.firstName} ${emp.lastName}`
                      : emp.name || emp.teamMemberName || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3 flex justify-end">
              <button
                onClick={fetchAttendanceRecords}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md transition-all"
              >
                <FiFileText />
                Apply Filters
              </button>
            </div>
          </div>

          {/* Records Table */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading records...</p>
            </div>
          ) : !Array.isArray(attendanceRecords) || attendanceRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiFileText className="mx-auto h-12 w-12 text-gray-400 mb-2" />
              <p>No attendance records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      In Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Out Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Working Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceRecords.map((record) => {
                    const empName = record.employee?.firstName && record.employee?.lastName
                      ? `${record.employee.firstName} ${record.employee.lastName}`
                      : record.employee?.name || record.employee?.teamMemberName || 'Unknown';

                    const statusOption = statusOptions.find(s => s.value === record.status);

                    return (
                      <tr key={record._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {empName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateForTable(record.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusOption?.color || 'bg-gray-100 text-gray-800'}`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.inTime ? new Date(record.inTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.outTime ? new Date(record.outTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.workingTime ? `${Math.floor(record.workingTime / 60)}h ${Math.round(record.workingTime % 60)}m` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEdit(record)}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1"
                          >
                            <FiEdit2 /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(record._id)}
                            className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2"> Instructions:</h3>
        <ul className="list-disc list-inside text-blue-800 space-y-1 text-sm">
          <li>Fields marked with * are required</li>
          <li>Department selection is required - employees will be filtered based on the selected department</li>
          <li>In Time and Out Time are mandatory for all attendance entries</li>
          <li>Use Add Entry mode for adding one record at a time</li>
          <li>Use View Records mode to see, edit, or delete existing attendance records</li>
          <li>System will prevent duplicate entries for the same employee and date</li>
        </ul>
      </div>
    </div>
  );
};

export default ManualAttendance;


